import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PortalSiteEntity } from './portal-site.entity';

export type PortalSiteDomainTlsStatus =
	| 'notApplicable'
	| 'pending'
	| 'issued'
	| 'failed';

/**
 * Whether a background provisioning/teardown job is currently in flight for this
 * domain. Drives the admin UI spinner and gates concurrent actions:
 *   - 'provisioning' — a PROVISION job is creating the CloudFront tenant /
 *     polling cert issuance / applying the cert.
 *   - 'removing'     — a DEPROVISION job is tearing the domain down. An
 *     in-flight PROVISION job sees this and aborts (delete wins).
 *   - null           — settled; no job running (the steady state, where
 *     `tlsStatus` is authoritative).
 */
export type PortalSiteDomainProvisioningState = 'provisioning' | 'removing';

/**
 * What Workd does with this address.
 *
 *   - 'storefront' — Workd SERVES the online store here. The customer's DNS points at Workd,
 *     which provisions a CloudFront distribution tenant and a certificate. One per brand.
 *   - 'linked'     — Workd does NOT serve it. The address belongs to one of the brand's
 *     websites (`fkWebsiteId`), and exists so Workd RECOGNIZES the address and knows which
 *     website is calling. That covers a site Workd builds but Vercel hosts, and a site Workd
 *     has nothing to do with, such as an agency's landing page. Several per brand.
 *   - 'booking'    — Workd SERVES it exactly as it serves a storefront address, but only booking
 *     links name it, so a tenant whose portal is `shop.acme.com` can send meeting links from
 *     `meet.acme.com`. It never becomes the address store links, emails or search engines use.
 *     One per brand. A portal site of its own was not an option: a brand has one storefront.
 *
 * An address is never itself "a website" — the website is the record it links to. Permissions
 * live on that website, so a site's many addresses share one place to maintain.
 *
 * Every kind claims the hostname in the DVP `_origin` map, so an address still belongs to
 * exactly one brand across all of Workd.
 *
 * Stored as text rather than a MySQL enum on purpose: database sync never ALTERS an existing
 * column, so a new kind needs no migration.
 */
export type PortalSiteDomainKind = 'storefront' | 'booking' | 'linked';

/** The kinds Workd hosts, so each needs CloudFront, a certificate and the spam-check widget. */
export const WORKD_SERVED_DOMAIN_KINDS: ReadonlyArray<PortalSiteDomainKind> = [
	'storefront',
	'booking',
];

// One row per hostname a portal_site answers on. isCustom = 0 is the derived
// workd URL (<alias>-<tenant>.my.workd.com) and is served by the shared
// wildcard CloudFront tenant; isCustom = 1 hostnames are the customer's own.
// `kind` then says whether Workd SERVES that address ('storefront' or 'booking',
// provisioned its own CloudFront distribution tenant + cert) or merely recognizes
// it ('linked', hosted elsewhere — no AWS work, no certificate), in which case
// `fk_website__id` names the website it belongs to.
//
// This table records only what the TENANT needs to know about a host: its
// TLS/CloudFront provisioning state. The hostname -> instance routing map
// lives in the DVP `_origin` table (login DB), keyed by hostname — crm-api
// registers/removes those rows over HTTP (login /origin) on validate/remove.
// There is intentionally no id handshake back to `_origin`: both register
// (on validate -> issued) and remove (by hostname) are idempotent.
@Index('UK_portal_site_domain_hostname', ['hostname'], { unique: true })
@Index('fk_portal_site__id', ['fkPortalSiteId'], {})
@Entity('portal_site_domain')
export class PortalSiteDomainEntity extends BaseEntity {
	@Column('int', { name: 'fk_portal_site__id', unsigned: true })
	public fkPortalSiteId!: number;

	@Column('varchar', { name: 'hostname', length: 150 })
	public hostname!: string;

	@Column({
		type: 'boolean',
		name: 'isCustom',
		width: 1,
		default: () => "'0'",
	})
	public isCustom!: boolean;

	// Defaulted so every row that predates this column reads as a storefront, which is what
	// they all are.
	@Column('varchar', {
		name: 'kind',
		length: 20,
		default: 'storefront',
	})
	public kind!: PortalSiteDomainKind;

	/**
	 * The website this address belongs to. Always set for a 'linked' address and never for one
	 * Workd serves itself, which grants nothing.
	 */
	@Column('int', { name: 'fk_website__id', nullable: true, unsigned: true })
	public fkWebsiteId!: number | null;

	@Column('enum', {
		name: 'tlsStatus',
		enum: ['notApplicable', 'pending', 'issued', 'failed'],
		default: 'notApplicable',
	})
	public tlsStatus!: PortalSiteDomainTlsStatus;

	@Column('varchar', { name: 'tlsCertArn', nullable: true, length: 256 })
	public tlsCertArn!: string | null;

	// CloudFront SaaS Manager distribution-tenant id for this custom hostname
	// (null for the derived workd URL, which is served by the shared wildcard
	// tenant). Stored so crm-api can poll status / delete the tenant later.
	@Column('varchar', {
		name: 'cfDistributionTenantId',
		nullable: true,
		length: 64,
	})
	public cfDistributionTenantId!: string | null;

	@Column({
		type: 'boolean',
		name: 'isPrimary',
		width: 1,
		default: () => "'0'",
	})
	public isPrimary!: boolean;

	@Column({
		type: 'boolean',
		name: 'isActive',
		width: 1,
		default: () => "'1'",
	})
	public isActive!: boolean;

	// Null when no background job is in flight (steady state). Set to
	// 'provisioning' / 'removing' while a queued job is working the domain, so
	// the admin UI can show a spinner that survives a page refresh and so a
	// delete can pre-empt an in-flight validate (see the type's doc comment).
	@Column('enum', {
		name: 'provisioningState',
		enum: ['provisioning', 'removing'],
		nullable: true,
		default: null,
	})
	public provisioningState!: PortalSiteDomainProvisioningState | null;

	@ManyToOne(
		() => PortalSiteEntity,
		(portalSite) => portalSite.portalSiteDomains,
		{
			onDelete: 'CASCADE',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: 'fk_portal_site__id', referencedColumnName: 'id' }])
	public fkPortalSite!: PortalSiteEntity;
}
